import React, { useEffect, useRef, useState } from "react";
import { Copy, MessageCircle, Share2 } from "lucide-react";

const ShareButton = ({ title, text }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const shareRef = useRef(null);

  const shareUrl =
    typeof window !== "undefined" ? window.location.href : "";
  const shareText = `${text || title || "Check this out"} ${shareUrl}`.trim();

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      if (shareRef.current && !shareRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!copied) return undefined;

    const timeoutId = window.setTimeout(() => {
      setCopied(false);
    }, 1800);

    return () => window.clearTimeout(timeoutId);
  }, [copied]);

  const handleCopyLink = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const helperInput = document.createElement("input");
        helperInput.value = shareUrl;
        document.body.appendChild(helperInput);
        helperInput.select();
        document.execCommand("copy");
        document.body.removeChild(helperInput);
      }

      setCopied(true);
      setIsOpen(false);
    } catch {
      setCopied(false);
    }
  };

  const handleShareWhatsApp = () => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
    setIsOpen(false);
  };

  const handleNativeShare = async () => {
    try {
      if (navigator?.share) {
        await navigator.share({
          title,
          text,
          url: shareUrl,
        });
      } else {
        await handleCopyLink();
        return;
      }
    } catch {
      return;
    }

    setIsOpen(false);
  };

  return (
    <div className="pd-share-wrap" ref={shareRef}>
      <button
        type="button"
        className={`pd-share-toggle ${isOpen ? "active" : ""}`}
        aria-label="Share this product"
        title="Share"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <Share2 size={18} />
      </button>

      {isOpen && (
        <div className="pd-share-menu">
          <button
            type="button"
            className="pd-share-option"
            onClick={handleShareWhatsApp}
          >
            <MessageCircle size={16} />
            WhatsApp
          </button>

          <button
            type="button"
            className="pd-share-option"
            onClick={handleCopyLink}
          >
            <Copy size={16} />
            {copied ? "Copied" : "Copy Link"}
          </button>

          <button
            type="button"
            className="pd-share-option"
            onClick={handleNativeShare}
          >
            <Share2 size={16} />
            Other
          </button>
        </div>
      )}
    </div>
  );
};

export default ShareButton;
